import React, { useEffect, useState } from "react";
import { Button, Typography } from "@material-ui/core";
import PublishIcon from "@material-ui/icons/Publish";
import { makeStyles } from "@material-ui/core/styles";
import FilterDate from "../FilterDate";
import Loader from "../Loader";
import useDynamicPeriodOfRecord from "../../pages/publicMap/hooks/useDynamicPeriodOfRecord";
import {
  LIVE_REBUILD_WARNING,
  LIVE_TREND_NOTE,
} from "../../pages/wqScorecard/scorecardCopy";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
  },
  heading: {
    marginBottom: theme.spacing(1),
  },
  lede: {
    marginBottom: theme.spacing(2),
  },
  // FiltersForm's own layout, so these sit on the same grid as every other
  // filter row on the site.
  row: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
  },
  action: {
    minHeight: 56,
    display: "flex",
    alignItems: "center",
  },
  note: {
    marginTop: theme.spacing(2),
  },
  warning: {
    marginTop: theme.spacing(1),
    color: theme.palette.warning.dark,
    fontWeight: 500,
  },
}));

// FilterDate speaks MM/dd/yyyy; the API and the period rows speak ISO.
const toPicker = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${m}/${d}/${y}` : "";
};
const toIso = (picker) => {
  if (!picker) return "";
  const [m, d, y] = String(picker).split("/");
  return y && m && d ? `${y}-${m}-${d}` : "";
};

/**
 * The dynamic window. The two pickers start from whichever period is selected,
 * so a snapshot doubles as a starting point you can nudge; applying writes the
 * dates to the site-wide analysis window and switches the page to Live.
 *
 * Two deliberate constraints:
 *   - editing a date never submits anything. This rebuild repoints the
 *     interactive map for every visitor and takes minutes, so it only happens
 *     on an explicit press of a button that says so.
 *   - Apply stays disabled until the dates differ from the window already in
 *     force, so browsing periods can never fire it.
 */
export default function ScorecardLiveDateControls({
  periodStart,
  periodEnd,
  liveStart,
  liveEnd,
  onApplied,
  disabled = false,
}) {
  const classes = useStyles();
  const [start, setStart] = useState(toPicker(periodStart));
  const [end, setEnd] = useState(toPicker(periodEnd));

  // Follow the selected period. Choosing a different one discards unapplied
  // edits, which is honest: the pickers describe that period.
  useEffect(() => {
    setStart(toPicker(periodStart));
    setEnd(toPicker(periodEnd));
  }, [periodStart, periodEnd]);

  const { handleSubmit, isSubmitLoading } = useDynamicPeriodOfRecord({
    startDate: toIso(start),
    endDate: toIso(end),
    refetchWqSummary: onApplied,
  });

  const isOrdered = Boolean(start && end) && toIso(start) <= toIso(end);
  const differsFromLive =
    Boolean(start && end) &&
    (toIso(start) !== String(liveStart ?? "").slice(0, 10) ||
      toIso(end) !== String(liveEnd ?? "").slice(0, 10));
  const canApply = isOrdered && differsFromLive && !disabled;

  return (
    <div className={classes.root}>
      <Typography variant="h6" component="h3" className={classes.heading}>
        Custom analysis window
      </Typography>
      <Typography variant="body2" className={classes.lede}>
        These dates start from the period selected above. Change them and apply
        to recalculate the site-wide analysis window; the scorecard will switch
        to Live to show the result.
      </Typography>

      <div className={classes.row}>
        <FilterDate
          label="Start date"
          name="scorecardLiveStart"
          value={start}
          onChange={(_, value) => setStart(value)}
        />
        <FilterDate
          label="End date"
          name="scorecardLiveEnd"
          value={end}
          onChange={(_, value) => setEnd(value)}
        />
        <div className={classes.action}>
          {isSubmitLoading ? (
            <Loader />
          ) : (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PublishIcon />}
              disabled={!canApply}
              onClick={handleSubmit}
            >
              Apply analysis window
            </Button>
          )}
        </div>
      </div>

      {!isOrdered && Boolean(start && end) && (
        <Typography variant="body2" className={classes.warning}>
          The end date is before the start date.
        </Typography>
      )}

      <Typography variant="body2" className={classes.warning}>
        {LIVE_REBUILD_WARNING}
      </Typography>
      <Typography variant="body2" className={classes.note}>
        {LIVE_TREND_NOTE}
      </Typography>
    </div>
  );
}
